/**
 * LSB Audio steganography — decode-only client-side implementation.
 *
 * Extracts payload from MP3 frame main_data byte LSBs.
 * Compatible with the Python LsbTechnique encoder.
 *
 * Encoding format:
 *   Frame traversal: file order, main_data bytes sequential within each frame
 *   Bit order: 1 LSB per main_data byte, MSB-first within each payload byte
 *   Layout:
 *     [0..31]   32 LSBs → uint32 BE: total blob length
 *     [32..N]   blob_length × 8 LSBs → blob bytes
 */

// MPEG1 Layer3 bitrates (kbps), indexed by bitrate_index (0x0–0xF)
const MPEG1_L3_BITRATES = [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 0];

// MPEG2/2.5 Layer3 bitrates (kbps)
const MPEG2_L3_BITRATES = [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160, 0];

// Sample rates by MPEG version
const SAMPLE_RATES: Record<number, number[]> = {
  3: [44100, 48000, 32000], // MPEG1
  2: [22050, 24000, 16000], // MPEG2
  0: [11025, 12000, 8000],  // MPEG2.5
};

interface FrameInfo {
  offset: number;
  frameSize: number;
  mainDataOffset: number;
  mainDataSize: number;
}

function parseFrameHeader(data: Uint8Array, offset: number): FrameInfo | null {
  if (offset + 4 > data.length) return null;

  // Check sync word (11 bits: 0xFFE0)
  if (data[offset] !== 0xff || (data[offset + 1]! & 0xe0) !== 0xe0) return null;

  const b1 = data[offset + 1]!;
  const b2 = data[offset + 2]!;

  const mpegVersion = (b1 >> 3) & 0x03; // 00=2.5, 01=reserved, 10=2, 11=1
  const layer = (b1 >> 1) & 0x03;       // 01=III, 10=II, 11=I
  const hasCrc = !(b1 & 0x01);          // 0=CRC present
  const bitrateIdx = (b2 >> 4) & 0x0f;
  const sampleRateIdx = (b2 >> 2) & 0x03;
  const padding = (b2 >> 1) & 0x01;
  const channelMode = (data[offset + 3]! >> 6) & 0x03; // 3=mono

  // Only support Layer III
  if (layer !== 0x01) return null;

  // Reserved version/sample_rate/bitrate
  if (mpegVersion === 1 || sampleRateIdx === 3 || bitrateIdx === 0 || bitrateIdx === 15)
    return null;

  const bitrate =
    mpegVersion === 3 ? MPEG1_L3_BITRATES[bitrateIdx]! : MPEG2_L3_BITRATES[bitrateIdx]!;

  const rates = SAMPLE_RATES[mpegVersion];
  if (!rates) return null;
  const sampleRate = rates[sampleRateIdx]!;

  const frameSize = Math.floor((144 * bitrate * 1000) / sampleRate) + padding;
  if (frameSize < 4) return null;

  const crcSize = hasCrc ? 2 : 0;
  let sideInfoSize: number;
  if (mpegVersion === 3) {
    sideInfoSize = channelMode === 3 ? 17 : 32;
  } else {
    sideInfoSize = channelMode === 3 ? 9 : 17;
  }

  const headerOverhead = 4 + crcSize + sideInfoSize;
  const mainDataSize = frameSize - headerOverhead;
  if (mainDataSize <= 0) return null;

  return {
    offset,
    frameSize,
    mainDataOffset: headerOverhead,
    mainDataSize,
  };
}

function parseMp3Frames(data: Uint8Array): FrameInfo[] {
  const frames: FrameInfo[] = [];
  let pos = 0;

  while (pos < data.length - 3) {
    if (data[pos] !== 0xff || (data[pos + 1]! & 0xe0) !== 0xe0) {
      pos++;
      continue;
    }

    const frame = parseFrameHeader(data, pos);
    if (!frame) {
      pos++;
      continue;
    }

    if (pos + frame.frameSize > data.length) break;

    frames.push(frame);
    pos += frame.frameSize;
  }

  return frames;
}

function collectMainDataOffsets(frames: FrameInfo[]): number[] {
  const offsets: number[] = [];
  for (const frame of frames) {
    const start = frame.offset + frame.mainDataOffset;
    for (let i = 0; i < frame.mainDataSize; i++) {
      offsets.push(start + i);
    }
  }
  return offsets;
}

function extractLsbBits(
  data: Uint8Array,
  offsets: number[],
  offsetBits: number,
  numBits: number
): Uint8Array {
  const result = new Uint8Array(numBits >> 3);
  for (let i = 0; i < numBits; i++) {
    const fileOffset = offsets[offsetBits + i]!;
    const bit = data[fileOffset]! & 1;
    const byteIdx = i >> 3;
    const bitPos = 7 - (i & 7); // MSB-first
    result[byteIdx]! |= bit << bitPos;
  }
  return result;
}

/**
 * Extract a blob from MP3 frame main_data LSBs.
 * Returns the blob bytes, or null if no valid payload found.
 */
export function extractLsbPayload(mp3Data: Uint8Array): Uint8Array | null {
  const frames = parseMp3Frames(mp3Data);
  if (frames.length === 0) return null;

  const offsets = collectMainDataOffsets(frames);

  // Need at least 32 bits for the length prefix
  if (offsets.length < 32) return null;

  // Extract length prefix (32 LSBs → 4 bytes → uint32 BE)
  const lengthBytes = extractLsbBits(mp3Data, offsets, 0, 32);
  const length = new DataView(lengthBytes.buffer).getUint32(0, false);

  // Validate length
  const maxCapacity = (offsets.length - 32) >> 3;
  if (length === 0 || length > maxCapacity) return null;

  // Extract payload
  return extractLsbBits(mp3Data, offsets, 32, length * 8);
}
